/**
 * Compositor del certificado de diente caído del Ratón Pérez.
 *
 * El diente de un niño es un hito, y lo único que queda de él es una moneda que
 * se gasta. Esto le da al padre un diploma con el nombre del niño, la fecha y el
 * número de pieza: algo que se imprime, se pega en la nevera y se guarda con los
 * veinte. La carta (`tooth-letter`) la deja el ratón esa noche; el certificado
 * es lo que sobrevive al día siguiente.
 *
 * Todo son funciones puras sobre strings: el render (canvas) y el formulario
 * viven aparte. El nombre del niño NUNCA sale del dispositivo — no hay llamada
 * de red en ningún punto de este flujo, a propósito, porque son datos de un
 * menor y no los necesitamos para nada.
 *
 * Hoy solo existe la variante es/raton-perez, que es la guía que lo usa.
 */

import type { ToothKind } from './tooth-letter'

export type ToothCertificateInput = {
  childName: string
  tooth: ToothKind
  /** Cuál de los veinte es. Se omite si el padre no lo sabe. */
  toothNumber?: number
  /** Fecha del diente en ISO (`yyyy-mm-dd`), tal cual la da un `<input type="date">`. */
  date?: string
  /** Cómo se cayó, en palabras del padre: "comiendo una manzana". */
  howItFell?: string
}

export type CertificateDetail = { label: string; value: string }

export type ToothCertificate = {
  title: string
  authority: string
  awardedTo: string
  childName: string
  citation: string
  details: CertificateDetail[]
  signature: string
  signatureRole: string
  /** Texto del sello, línea a línea (se pinta dentro de un círculo). */
  seal: string[]
}

/** Nombre de la pieza en el certificado, por tipo de diente. */
export const CERTIFICATE_TOOTH_LABEL: Record<ToothKind, string> = {
  first: 'Primer diente de leche',
  next: 'Diente de leche',
  molar: 'Muela de leche',
}

const CITATION: Record<ToothKind, string> = {
  first:
    'por haber entregado su primer diente de leche con una valentía digna de mención, y por haberlo dejado tan reluciente que se encontró a la primera.',
  next: 'por haber entregado otro diente de leche en perfecto estado, cepillado como manda esta casa.',
  molar:
    'por haber entregado una muela — de las más difíciles, y de las que más pesan en el saco del ratón — sin perder la sonrisa.',
}

const COPY = {
  title: 'Certificado de diente caído',
  authority: 'Casa Oficial del Ratón Pérez',
  awardedTo: 'Se concede a',
  signature: 'Ratón Pérez',
  signatureRole: 'Recogedor jefe de dientes de leche',
  seal: ['Sello de la', 'Casa del', 'Ratón Pérez'],
  pieceLabel: 'Pieza',
  dateLabel: 'Fecha',
  howItFellLabel: 'Cómo se cayó',
  registryLabel: 'Expediente',
  /** Los dientes de leche son veinte; numerar la pieza los sitúa en la serie. */
  pieceOf: '{label} n.º {number} de {total}',
} as const

/** Total de dientes de leche: la serie que el certificado va numerando. */
export const BABY_TEETH_TOTAL = 20

const MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
]

/**
 * Fecha ISO → "12 de julio de 2026". Devuelve `null` si no la entiende: el
 * certificado prefiere quedarse sin fila de fecha a imprimir una fecha rara.
 *
 * Se parte a mano en vez de construir un `Date`: `new Date('2026-01-01')` es
 * medianoche UTC, así que en América `getDate()` devolvería el 31 de diciembre
 * y el certificado dataría el diente el día anterior.
 */
export function formatCertificateDate(iso: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!match) return null

  const [year, month, day] = match.slice(1).map(Number)
  // Roundtrip en UTC: descarta un 30 de febrero, que el regex sí acepta.
  const date = new Date(Date.UTC(year, month - 1, day))
  const isReal =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  if (!isReal) return null

  return `${day} de ${MONTHS[month - 1]} de ${year}`
}

/**
 * Número de expediente del certificado.
 *
 * Derivado del nombre y del diente en vez de aleatorio: si el padre reimprime,
 * el ratón no se contradice, y dos dientes del mismo niño llevan expedientes
 * distintos. Además lo hace testeable sin controlar el generador aleatorio.
 */
export function certificateNumberFor(childName: string, tooth: ToothKind): string {
  const seed = [...childName.trim().toLowerCase()].reduce(
    (total, character) => total + character.charCodeAt(0),
    0
  )
  const toothOffset = { first: 0, next: 1, molar: 2 }[tooth]
  const number = (seed * 7 + toothOffset * 1373) % 10000
  return `RP-${String(number).padStart(4, '0')}`
}

/** Texto opcional del padre: se ignora si viene vacío o solo con espacios. */
function filled(value: string | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/** Fuera de la serie de veinte no es un número de pieza: se omite la numeración. */
function pieceLabelFor(tooth: ToothKind, toothNumber: number | undefined): string {
  const label = CERTIFICATE_TOOTH_LABEL[tooth]
  const isInSeries =
    toothNumber !== undefined &&
    Number.isInteger(toothNumber) &&
    toothNumber >= 1 &&
    toothNumber <= BABY_TEETH_TOTAL
  if (!isInSeries) return label

  return COPY.pieceOf
    .replace('{label}', label)
    .replace('{number}', String(toothNumber))
    .replace('{total}', String(BABY_TEETH_TOTAL))
}

function detailsFor(input: ToothCertificateInput, childName: string): CertificateDetail[] {
  const details: CertificateDetail[] = [
    { label: COPY.pieceLabel, value: pieceLabelFor(input.tooth, input.toothNumber) },
  ]

  const date = filled(input.date)
  const formattedDate = date ? formatCertificateDate(date) : null
  if (formattedDate) details.push({ label: COPY.dateLabel, value: formattedDate })

  const howItFell = filled(input.howItFell)
  if (howItFell) details.push({ label: COPY.howItFellLabel, value: howItFell })

  details.push({
    label: COPY.registryLabel,
    value: certificateNumberFor(childName, input.tooth),
  })
  return details
}

export function composeToothCertificate(input: ToothCertificateInput): ToothCertificate {
  const childName = input.childName.trim()
  return {
    title: COPY.title,
    authority: COPY.authority,
    awardedTo: COPY.awardedTo,
    childName,
    citation: CITATION[input.tooth],
    details: detailsFor(input, childName),
    signature: COPY.signature,
    signatureRole: COPY.signatureRole,
    seal: [...COPY.seal],
  }
}

/** Nombre de fichero de la descarga. Sin el nombre del niño: acaba en su carrete. */
export function certificateFilename(): string {
  return 'certificado-del-raton-perez.png'
}
